import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ApiService, GetRequestArguments, PostRequestArguments, Refresh } from './api.service';
import {BehaviorSubject, forkJoin, Observable} from 'rxjs';
import {UntypedFormBuilder, Validators} from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  public $allUsers = new BehaviorSubject<any>([]);
  public $groups = new BehaviorSubject(undefined);
  public $scopes = new BehaviorSubject([]);
  public $currentUser = new BehaviorSubject<any>([]);
  public $userDetails = new BehaviorSubject<any>([]);

  public $profiles = new BehaviorSubject([]);
  public $editing = new BehaviorSubject(undefined);

  private url = 'user';
  private profileUrl = 'profile';
  private manageesUrl = 'manager/managees';
  private groupKey = 'user';
  private hour = 60 * 60;

  constructor(private api: ApiService,
              private formBuilder: UntypedFormBuilder) {
  }

  loadAll(refresh: Refresh = 'expired') {
    return new Promise<void>((resolve, reject) => {
      forkJoin(
        [
          this.loadUsers(refresh, true),
        ]
      ).subscribe(
        res => { resolve(); },
        error => { reject(); }
      );
    });
  }

  public loadScopes(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: 'scopes',
      cacheGroup: this.groupKey,
      ttl: this.hour,
      refresh,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(res => {
      this.api.updateObservable(res, this.$scopes);
    });
    return call;
  }

  public loadUser(refresh: Refresh = 'expired', id, $with: Array<string> = []) {
    let params = new HttpParams();
    if ($with.length > 0) {
      params = params.set('with', $with.toString());
    }
    const args = new GetRequestArguments({
      url: [this.url, id],
      cacheGroup: this.groupKey,
      cacheOffset: ($with.length > 0) ? '/' + $with.toString() : '',
      ttl: this.hour,
      params,
      refresh,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(res => {
      this.api.updateObservable(res, this.$userDetails);
    });
    return call;
  }

  public loadUsers(refresh: Refresh = 'expired', getAll: boolean = true) {
    const args = new GetRequestArguments({
      url: this.url,
      cacheGroup: this.groupKey,
      ttl: this.hour,
      refresh,
      getAll,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(res => {
      this.api.updateObservable(res, this.$allUsers);
    });
    return call;
  }

  public loadUsersByDateRange(refresh: Refresh = 'expired', getAll: boolean = false, table, from, to) : Observable<any> {
    let params = new HttpParams();
    params = params.set('table', table);
    params = params.set('from', from);
    params  = params.set('to', to);
    const args = new GetRequestArguments({
      url: `${this.url}/date`,
      cacheGroup: this.groupKey,
      ttl: this.hour,
      refresh,
      params,
      getAll,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(res => {
      this.api.updateObservable(res, this.$allUsers);
    });
    return call;
  }

  loadCurrentUser(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: this.url,
      cacheGroup: this.groupKey,
      ttl: this.hour,
      refresh,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(res => {
      this.api.updateObservable(res, this.$currentUser);
    });
    return call;
  }

  getAll(refresh: Refresh = 'expired', getAll: boolean = false, $with: Array<string> = []) {
    let params = new HttpParams();
    if ($with.length > 0) {
      params = params.set('with', $with.toString());
    }
    const args = new GetRequestArguments({
      url: this.url,
      cacheGroup: this.groupKey,
      cacheOffset: ($with.length > 0) ? '/' + $with.toString() : '',
      ttl: this.hour * 24,
      params,
      refresh,
      getAll,
      delayed: false,
    });
    const request = this.api.cachedGetRequest(args);
    request.subscribe(res => { this.api.updateObservable(res, this.$allUsers); });
    return request;
  }

  updateProfile(profile: FormData) {
    const args = new PostRequestArguments({
      url: this.profileUrl,
      object: profile,
    });
    this.api.clearCacheGroup(this.groupKey);
    const request = this.api.postRequest(args);
    request.subscribe(res => {
      this.api.updateObservable(res, this.$profiles);
    });
    return request;
  }

  getManagees(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: this.manageesUrl,
      cacheGroup: this.groupKey,
      ttl: this.hour,
      refresh,
    });
    return this.api.cachedGetRequest(args);
  }

  createProfileForm() {
    return this.formBuilder.group({
      id: [undefined, [Validators.required]],
      user_id: [undefined, [Validators.required]],
      profile_picture: [undefined, [Validators.required]],
      position: [undefined, []],
      name: [undefined, []],
      surname: [undefined, []],
      linkedIn: [undefined, []],
      additional_data: [undefined, []],
      manager: [undefined, []],
      manager_id: [undefined, []],
      email: [undefined, []],
      business_unit: [undefined, []],
      birthday: [undefined, []],
      phone: [undefined, []],
      employment_country: [undefined, []]
    });
  }

  fillProfileForm(form, profile) {
    form.controls.id.patchValue(profile.id);
    form.controls.user_id.patchValue(profile.user_id);
    form.controls.profile_picture.patchValue(profile.profile_picture);
    form.controls.position.patchValue(profile.position);
    form.controls.name.patchValue(profile.name);
    form.controls.surname.patchValue(profile.surname);
    form.controls.linkedIn.patchValue(profile.linkedIn);
    form.controls.additional_data.patchValue(profile.additional_data);
    form.controls.manager.patchValue(profile.manager);
    form.controls.manager_id.patchValue(profile.manager_id);
    form.controls.email.patchValue(profile.email);
    form.controls.business_unit.patchValue(profile.business_unit);
    form.controls.birthday.patchValue(profile.birthday);
    form.controls.phone.patchValue(profile.phone);
    form.controls.employment_country.patchValue(profile.employment_country);
  }

  createFormData(form) {
    return this.api.createFormData(form.value);
  }
}
